/**
 * ServiceCategoriesTab — the customer-platform service hub (bilingual).
 *
 * 7 cards (Feb 2026 restructure):
 *   1. الطباعة وتخليص المستندات      → /control/cloudgm/service-requests/printing-docs
 *   2. تأسيس الشركات                 → /control/cloudgm/service-requests/company-formation
 *   3. خدمات الوكيل الضريبي         → /control/cloudgm/service-requests/tax-agent
 *   4. خدمات دفع الفواتير           → /control/cloudgm/service-requests/bill-payment
 *   5. خدمات الترجمة القانونية       → /control/cloudgm/service-requests/legal-translation
 *   6. الترجمة القانونية (AI)         → /dashboard/cloudgm/legal-translation
 *   7. إضافة طلب لعميل (يدوي)         → /dashboard/cloudgm/assigned-tasks/new?target=customer
 *
 * "طلبات التأشيرة والهوية" lives INSIDE الطباعة وتخليص المستندات (per customer platform).
 */
import { Briefcase, FileCheck, Languages, Wallet, Calculator, Sparkles, UserPlus } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';

const CARDS = [
  {
    id: 'printing-docs',
    path: '/control/cloudgm/service-requests/printing-docs',
    icon: FileCheck,
    titleAr: 'الطباعة وتخليص المستندات',
    titleEn: 'Printing & Document Clearing',
    descAr: 'التأشيرة والهوية، التأشيرات السياحية، تصديق المستندات، رخصة القيادة',
    descEn: 'Visa & Emirates ID, tourist visas, document certification, driving license',
    iconBg: 'bg-amber-500/20',
    iconColor: 'text-amber-500',
    borderHover: 'hover:border-amber-500/50',
    testId: 'sr-printing-docs',
  },
  {
    id: 'company-formation',
    path: '/control/cloudgm/service-requests/company-formation',
    icon: Briefcase,
    titleAr: 'تأسيس الشركات',
    titleEn: 'Company Formation',
    descAr: 'طلبات ومحادثات تأسيس الشركات',
    descEn: 'Company formation requests & conversations',
    iconBg: 'bg-emerald-500/20',
    iconColor: 'text-emerald-500',
    borderHover: 'hover:border-emerald-500/50',
    testId: 'sr-company-formation',
  },
  {
    id: 'tax-agent',
    path: '/control/cloudgm/service-requests/tax-agent',
    icon: Calculator,
    titleAr: 'خدمات الوكيل الضريبي',
    titleEn: 'Tax Agent Services',
    descAr: 'طلبات ومحادثات الوكيل الضريبي',
    descEn: 'Tax agent requests & conversations',
    iconBg: 'bg-purple-500/20',
    iconColor: 'text-purple-500',
    borderHover: 'hover:border-purple-500/50',
    testId: 'sr-tax-agent',
  },
  {
    id: 'bill-payment',
    path: '/control/cloudgm/service-requests/bill-payment',
    icon: Wallet,
    titleAr: 'خدمات دفع الفواتير',
    titleEn: 'Bill Payment Services',
    descAr: 'طلبات ومحادثات دفع الفواتير',
    descEn: 'Bill payment requests & conversations',
    iconBg: 'bg-pink-500/20',
    iconColor: 'text-pink-500',
    borderHover: 'hover:border-pink-500/50',
    testId: 'sr-bill-payment',
  },
  {
    id: 'legal-translation',
    path: '/control/cloudgm/service-requests/legal-translation',
    icon: Languages,
    titleAr: 'خدمات الترجمة القانونية',
    titleEn: 'Legal Translation Services',
    descAr: 'طلبات ومحادثات الترجمة القانونية',
    descEn: 'Legal translation requests & conversations',
    iconBg: 'bg-blue-500/20',
    iconColor: 'text-blue-500',
    borderHover: 'hover:border-blue-500/50',
    testId: 'sr-legal-translation',
  },
  // ── Moved here from the top-level Dashboard (Feb 2026) ──
  {
    id: 'legal-translation-ai',
    path: '/dashboard/cloudgm/legal-translation',
    icon: Sparkles,
    titleAr: 'الترجمة القانونية (AI)',
    titleEn: 'Legal Translation (AI)',
    descAr: 'أداة الترجمة الذكية للمستندات القانونية بـ 15 لغة مع تصدير PDF',
    descEn: 'AI tool for legal documents in 15 languages with PDF export',
    iconBg: 'bg-rose-500/20',
    iconColor: 'text-rose-500',
    borderHover: 'hover:border-rose-500/50',
    testId: 'sr-legal-translation-ai',
  },
  {
    id: 'add-manual-customer-request',
    path: '/dashboard/cloudgm/assigned-tasks/new?target=customer',
    icon: UserPlus,
    titleAr: 'إضافة طلب لعميل (يدوي)',
    titleEn: 'Add Manual Customer Request',
    descAr: 'سجّل طلب عميل جاء عبر الواتساب/الهاتف يدوياً — يُنشئ طلب برقم CUST-XXXXXX',
    descEn: 'Log a customer request received via WhatsApp/phone manually — creates a CUST-XXXXXX request',
    iconBg: 'bg-cyan-500/20',
    iconColor: 'text-cyan-400',
    borderHover: 'hover:border-cyan-500/50',
    testId: 'sr-add-manual-customer-request',
  },
];

export default function ServiceCategoriesTab({ navigate }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-6">
        {isAr ? 'طلبات الخدمة' : 'Service Requests'}
      </h2>
      <p className="text-[#808080] mb-6">
        {isAr
          ? 'خدمات منصة العملاء — اختر الخدمة لإدارة طلباتها أو أضف طلباً يدوياً'
          : 'Customer-platform services — pick a service to manage its requests or add one manually'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {CARDS.map(({ id, path, icon: Icon, titleAr, titleEn, descAr, descEn, iconBg, iconColor, borderHover, testId }) => (
          <div
            key={id}
            onClick={() => navigate(path)}
            className={`group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-8 border border-white/10 ${borderHover} transition-all cursor-pointer hover:-translate-y-1 hover:bg-white/[0.04]`}
            data-testid={testId}
          >
            <div className="text-center">
              <div className={`w-16 h-16 ${iconBg} rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                <Icon className={`h-8 w-8 ${iconColor}`} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{isAr ? titleAr : titleEn}</h3>
              <p className="text-sm text-[#808080]">{isAr ? descAr : descEn}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
